// frontend/src/samkalp-admin/tabs/MockTestsTab.js
//
// Admin Mock Test management: create tests, add questions, publish/
// unpublish. Same tab pattern as PyqBankTab.js.
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Form, Button, Alert, Badge, Row, Col } from 'react-bootstrap';
import { Trash2, Eye, EyeOff, Plus } from 'lucide-react';
import api from '../../api/api';
import { getTenantProjects } from '../../api/api';
import { useAlert } from './shared';

const EMPTY_TEST_FORM = { title: '', exam_type: 'Prelims', subject_project_id: '', duration_min: 60, total_marks: 0, negative_marking: 0 };
const EMPTY_Q_FORM = { question_text: '', optA: '', optB: '', optC: '', optD: '', answer_key: 'A', marks: 2 };
const EMPTY_BUILD_FORM = { blueprintKey: '', title: '', duration_min: 60, negative_marking: 0, include_historical: true, include_predicted: true };

const MockTestsTab = ({ slug }) => {
    const [projects, setProjects] = useState([]);
    const [tests, setTests] = useState([]);
    const [alert, showAlert] = useAlert();
    const [testForm, setTestForm] = useState(EMPTY_TEST_FORM);
    const [creating, setCreating] = useState(false);

    const [selectedTest, setSelectedTest] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [qForm, setQForm] = useState(EMPTY_Q_FORM);
    const [addingQ, setAddingQ] = useState(false);

    const [approvedBlueprints, setApprovedBlueprints] = useState([]);
    const [buildForm, setBuildForm] = useState(EMPTY_BUILD_FORM);
    const [building, setBuilding] = useState(false);

    const loadTests = useCallback(() => {
        api.get(`/tenant/${slug}/askos/admin/mock-tests`).then(res => setTests(res.data)).catch(() => {});
    }, [slug]);

    const loadApprovedBlueprints = useCallback(() => {
        api.get(`/tenant/${slug}/askos/admin/pyq/blueprints`).then(res => {
            setApprovedBlueprints((res.data || []).filter(b => b.status === 'approved'));
        }).catch(() => setApprovedBlueprints([]));
    }, [slug]);

    useEffect(() => {
        getTenantProjects(slug).then(res => setProjects(res.data || [])).catch(() => setProjects([]));
        loadTests();
        loadApprovedBlueprints();
    }, [slug, loadTests, loadApprovedBlueprints]);

    const loadQuestions = useCallback((testId) => {
        api.get(`/tenant/${slug}/askos/admin/mock-tests/${testId}/questions`).then(res => setQuestions(res.data)).catch(() => setQuestions([]));
    }, [slug]);

    const selectTest = (test) => {
        setSelectedTest(test);
        loadQuestions(test.id);
    };

    const createTest = async () => {
        if (!testForm.title.trim()) { showAlert('Title is required.', 'danger'); return; }
        setCreating(true);
        try {
            await api.post(`/tenant/${slug}/askos/admin/mock-tests`, {
                title: testForm.title,
                exam_type: testForm.exam_type,
                subject_project_id: testForm.subject_project_id ? Number(testForm.subject_project_id) : null,
                duration_min: Number(testForm.duration_min),
                total_marks: Number(testForm.total_marks),
                negative_marking: Number(testForm.negative_marking),
            });
            showAlert('Mock test created.');
            setTestForm(EMPTY_TEST_FORM);
            loadTests();
        } catch (err) {
            showAlert(err.response?.data?.detail || 'Create failed.', 'danger');
        } finally {
            setCreating(false);
        }
    };

    const togglePublish = async (t) => {
        try {
            await api.patch(`/tenant/${slug}/askos/admin/mock-tests/${t.id}/publish`, { is_published: !t.is_published });
            loadTests();
        } catch {
            showAlert('Could not update.', 'danger');
        }
    };

    const handleBuildFromBlueprint = async () => {
        const bp = approvedBlueprints.find(b => `${b.subject_project_id}|${b.paper}` === buildForm.blueprintKey);
        if (!bp) { showAlert('Select a subject/paper with an approved blueprint.', 'danger'); return; }
        if (!buildForm.title.trim()) { showAlert('Title is required.', 'danger'); return; }
        setBuilding(true);
        try {
            const res = await api.post(`/tenant/${slug}/askos/admin/mock-tests/build-from-blueprint`, {
                subject_project_id: bp.subject_project_id,
                paper: bp.paper,
                title: buildForm.title,
                duration_min: Number(buildForm.duration_min),
                negative_marking: Number(buildForm.negative_marking),
                include_historical: buildForm.include_historical,
                include_predicted: buildForm.include_predicted,
            });
            const empties = res.data.empty_groups?.length ? ` (no questions available for: ${res.data.empty_groups.join(', ')})` : '';
            showAlert(`Built mock test with ${res.data.question_count} questions${empties}.`);
            setBuildForm(EMPTY_BUILD_FORM);
            loadTests();
        } catch (err) {
            showAlert(err.response?.data?.detail || 'Build failed.', 'danger');
        } finally {
            setBuilding(false);
        }
    };

    const deleteTest = async (t) => {
        if (!window.confirm(`Delete "${t.title}" and all its questions?`)) return;
        try {
            await api.delete(`/tenant/${slug}/askos/admin/mock-tests/${t.id}`);
            if (selectedTest?.id === t.id) setSelectedTest(null);
            loadTests();
        } catch {
            showAlert('Delete failed.', 'danger');
        }
    };

    const addQuestion = async () => {
        if (!qForm.question_text.trim()) { showAlert('Question text is required.', 'danger'); return; }
        setAddingQ(true);
        try {
            const options = { A: qForm.optA, B: qForm.optB, C: qForm.optC, D: qForm.optD };
            await api.post(`/tenant/${slug}/askos/admin/mock-tests/${selectedTest.id}/questions`, {
                question_text: qForm.question_text,
                options,
                answer_key: qForm.answer_key,
                marks: Number(qForm.marks),
            });
            setQForm(EMPTY_Q_FORM);
            loadQuestions(selectedTest.id);
            loadTests();
        } catch (err) {
            showAlert(err.response?.data?.detail || 'Could not add question.', 'danger');
        } finally {
            setAddingQ(false);
        }
    };

    const deleteQuestion = async (q) => {
        if (!window.confirm('Delete this question?')) return;
        try {
            await api.delete(`/tenant/${slug}/askos/admin/mock-tests/questions/${q.id}`);
            loadQuestions(selectedTest.id);
            loadTests();
        } catch {
            showAlert('Delete failed.', 'danger');
        }
    };

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small">{alert.msg}</Alert>}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <h6 className="fw-bold mb-3">Create Mock Test</h6>
                    <Row className="g-2 mb-2">
                        <Col md={4}>
                            <Form.Control size="sm" placeholder="Title" value={testForm.title} onChange={e => setTestForm(f => ({ ...f, title: e.target.value }))} />
                        </Col>
                        <Col md={2}>
                            <Form.Select size="sm" value={testForm.exam_type} onChange={e => setTestForm(f => ({ ...f, exam_type: e.target.value }))}>
                                {['Prelims', 'Mains', 'GS1', 'GS2', 'GS3', 'GS4', 'CSAT'].map(t => <option key={t} value={t}>{t}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Select size="sm" value={testForm.subject_project_id} onChange={e => setTestForm(f => ({ ...f, subject_project_id: e.target.value }))}>
                                <option value="">All subjects (global)</option>
                                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={1}>
                            <Form.Control size="sm" type="number" title="Duration (min)" value={testForm.duration_min} onChange={e => setTestForm(f => ({ ...f, duration_min: e.target.value }))} />
                        </Col>
                        <Col md={1}>
                            <Form.Control size="sm" type="number" step="0.1" title="Total marks" value={testForm.total_marks} onChange={e => setTestForm(f => ({ ...f, total_marks: e.target.value }))} />
                        </Col>
                        <Col md={1}>
                            <Form.Control size="sm" type="number" step="0.01" title="Negative marking" value={testForm.negative_marking} onChange={e => setTestForm(f => ({ ...f, negative_marking: e.target.value }))} />
                        </Col>
                    </Row>
                    <Form.Text className="text-muted">Duration (min) · Total marks · Negative marking per wrong answer, left to right.</Form.Text>
                    <div className="mt-2">
                        <Button size="sm" onClick={createTest} disabled={creating}>Create</Button>
                    </div>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <h6 className="fw-bold mb-3">Build from Blueprint</h6>
                    <p className="small text-muted">Assembles a test whose questions and choice-structure mirror the real paper — only subjects/papers with an approved QP Blueprint (PYQ Bank tab) are selectable.</p>
                    {approvedBlueprints.length === 0 ? (
                        <p className="small text-muted fst-italic">No approved blueprints yet — generate and approve one in the PYQ Bank tab first.</p>
                    ) : (
                        <>
                            <Row className="g-2 mb-2">
                                <Col md={4}>
                                    <Form.Select size="sm" value={buildForm.blueprintKey} onChange={e => setBuildForm(f => ({ ...f, blueprintKey: e.target.value }))}>
                                        <option value="">Select subject / paper…</option>
                                        {approvedBlueprints.map(b => {
                                            const key = `${b.subject_project_id}|${b.paper}`;
                                            const subjectName = projects.find(p => p.id === b.subject_project_id)?.name || `Subject ${b.subject_project_id}`;
                                            return <option key={key} value={key}>{subjectName} — {b.paper}</option>;
                                        })}
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Control size="sm" placeholder="Title" value={buildForm.title} onChange={e => setBuildForm(f => ({ ...f, title: e.target.value }))} />
                                </Col>
                                <Col md={2}>
                                    <Form.Control size="sm" type="number" title="Duration (min)" value={buildForm.duration_min} onChange={e => setBuildForm(f => ({ ...f, duration_min: e.target.value }))} />
                                </Col>
                                <Col md={2}>
                                    <Form.Control size="sm" type="number" step="0.01" title="Negative marking" value={buildForm.negative_marking} onChange={e => setBuildForm(f => ({ ...f, negative_marking: e.target.value }))} />
                                </Col>
                            </Row>
                            <div className="d-flex align-items-center gap-3 mb-2">
                                <Form.Check inline type="checkbox" label="Include historical" checked={buildForm.include_historical} onChange={e => setBuildForm(f => ({ ...f, include_historical: e.target.checked }))} />
                                <Form.Check inline type="checkbox" label="Include predicted" checked={buildForm.include_predicted} onChange={e => setBuildForm(f => ({ ...f, include_predicted: e.target.checked }))} />
                            </div>
                            <Button size="sm" onClick={handleBuildFromBlueprint} disabled={building}>Build Test</Button>
                        </>
                    )}
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <h6 className="fw-bold mb-3">Mock Tests ({tests.length})</h6>
                    <Table hover responsive size="sm" className="align-middle">
                        <thead className="table-light">
                            <tr><th>Title</th><th>Type</th><th>Questions</th><th>Duration</th><th>Published</th><th className="text-end">Actions</th></tr>
                        </thead>
                        <tbody>
                            {tests.map(t => (
                                <tr key={t.id} onClick={() => selectTest(t)} style={{ cursor: 'pointer', background: selectedTest?.id === t.id ? '#f0f4ff' : undefined }}>
                                    <td className="x-small fw-semibold">{t.title}</td>
                                    <td><Badge bg="light" text="dark" className="border">{t.exam_type || '—'}</Badge></td>
                                    <td className="x-small">{t.question_count}</td>
                                    <td className="x-small">{t.duration_min}m</td>
                                    <td>{t.is_published ? <Badge bg="success">published</Badge> : <Badge bg="secondary">hidden</Badge>}</td>
                                    <td className="text-end" onClick={e => e.stopPropagation()}>
                                        <Button variant="link" size="sm" className="p-1" title={t.is_published ? 'Unpublish' : 'Publish'} onClick={() => togglePublish(t)}>
                                            {t.is_published ? <EyeOff size={14} /> : <Eye size={14} />}
                                        </Button>
                                        <Button variant="link" size="sm" className="p-1 text-danger" title="Delete" onClick={() => deleteTest(t)}>
                                            <Trash2 size={14} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {tests.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-3">No mock tests yet.</td></tr>}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {selectedTest && (
                <Card className="border-0 shadow-sm">
                    <Card.Body>
                        <h6 className="fw-bold mb-3">Questions for "{selectedTest.title}" ({questions.length})</h6>

                        <Row className="g-2 mb-2">
                            <Col md={12}>
                                <Form.Control size="sm" placeholder="Question text" value={qForm.question_text} onChange={e => setQForm(f => ({ ...f, question_text: e.target.value }))} />
                            </Col>
                        </Row>
                        <Row className="g-2 mb-2">
                            {['A', 'B', 'C', 'D'].map(opt => (
                                <Col md={3} key={opt}>
                                    <Form.Control size="sm" placeholder={`Option ${opt}`} value={qForm[`opt${opt}`]} onChange={e => setQForm(f => ({ ...f, [`opt${opt}`]: e.target.value }))} />
                                </Col>
                            ))}
                        </Row>
                        <Row className="g-2 mb-2 align-items-center">
                            <Col md={2}>
                                <Form.Select size="sm" value={qForm.answer_key} onChange={e => setQForm(f => ({ ...f, answer_key: e.target.value }))}>
                                    {['A', 'B', 'C', 'D'].map(o => <option key={o} value={o}>Answer: {o}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={2}>
                                <Form.Control size="sm" type="number" step="0.5" placeholder="Marks" value={qForm.marks} onChange={e => setQForm(f => ({ ...f, marks: e.target.value }))} />
                            </Col>
                            <Col md={3}>
                                <Button size="sm" onClick={addQuestion} disabled={addingQ}><Plus size={14} className="me-1" />Add Question</Button>
                            </Col>
                        </Row>

                        <Table size="sm" className="mt-3 mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                            <colgroup>
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '42%' }} />
                                <col style={{ width: '30%' }} />
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '10%' }} />
                            </colgroup>
                            <thead><tr><th>#</th><th>Question</th><th>Answer</th><th>Marks</th><th className="text-end">Actions</th></tr></thead>
                            <tbody>
                                {questions.map(q => (
                                    <tr key={q.id}>
                                        <td className="x-small">
                                            {q.question_number ? `Q${q.question_number}${q.sub_part ? `(${q.sub_part})` : ''}` : '—'}
                                            {q.choice_group_label && <Badge bg="light" text="dark" className="border ms-1 d-block mt-1" style={{ whiteSpace: 'normal' }}>{q.choice_group_label}</Badge>}
                                        </td>
                                        <td className="x-small" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{q.question_text}</td>
                                        <td className="x-small" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                            {q.options
                                                ? <Badge bg="light" text="dark" className="border">{q.answer_key || '—'}</Badge>
                                                : (q.answer_key || <span className="text-muted">—</span>)}
                                        </td>
                                        <td className="x-small">{q.marks}</td>
                                        <td className="text-end">
                                            <Button variant="link" size="sm" className="p-1 text-danger" onClick={() => deleteQuestion(q)}><Trash2 size={14} /></Button>
                                        </td>
                                    </tr>
                                ))}
                                {questions.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-3">No questions yet.</td></tr>}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}
        </>
    );
};

export default MockTestsTab;
